import { AfterContentChecked, ChangeDetectorRef, Component, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin } from 'rxjs';
import { DialogService } from '../../_services/dialog.service';
import { VendorService } from '../../_services/fundraising/vendor.service';
import { formatDateTime } from '../../_shared/utils';
import { AuthService } from '../../_services/auth.service';
import { DatePipe } from '@angular/common';
import { PledgePerWinService } from '../../_services/fundraising/pledgePerWin.service';
import { PledgePerWin } from '../../_models/fundraising/ppw/PledgePerWin';
import { PledgePerWinPricingTier } from '../../_models/fundraising/ppw/PledgePerWinPricingTier';

@Component({
  selector: 'app-ppw-create',
  templateUrl: './ppw-create.component.html',
  styleUrls: ['../cue5050/cue5050-create.component.css']
})
export class PledgePerWinCreateComponent implements OnInit, AfterContentChecked {

  public form: FormGroup;

  public formControls(name: string)  {
    return this.form.controls[name];
  }

  public eventFormControls(name: string)  {
    return (this.form.controls.event as FormGroup)?.controls[name];
  }

  public ppw: PledgePerWin;
  public eventId: number;
  public vendorName: string;

  public hasValidationErrors: boolean;
  public showAdvancedOptions: boolean;

  public teams = [];

  public cloning = false;
  public viewing = false;

  public counter = new Array(100);

  public ppwCopyPlaceholders = {

    newSubscriberEmailMessage: "Thanks for subscribing to our Pledge Per Win fundraiser! We appreciate your support.",
    gameOverEmailMessage: "Game Over! This win is dedicated to our adoring fans and supporters!",
    subscriptionChargeEmailMessage: "Thanks for supporting our Pledge Per Win fundraiser! Your card has been charged and you'll find more details in this email below.",
    gameOverTextMessage: "Game Over! This win is dedicated to our adoring fans and supporters!",
    subscriptionChargeTextMessage: "Thanks for supporting our Pledge Per Win fundraiser! Your card has been charged. Find more details in an email sent to the address you used when you signed up.",
    customCheckoutCopty: "Thanks for subscribing to our Pledge Per Win fundraiser! Go Team!"
  }

  constructor(
    private ppwService: PledgePerWinService,
    private vendorService: VendorService,
    private authService: AuthService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef,
    private cdRef: ChangeDetectorRef,
    private datePipe: DatePipe
  ) { }

  ngAfterContentChecked(): void {
    this.cdRef.detectChanges()
  }

  ngOnInit(): void {
    this.route.params
      .subscribe(
        params => {

          this.eventId = params['id'];

          const ppwObs = this.ppwService.getById(this.eventId ?? 0);
          const teamObs = this.ppwService.getAllTeams();

          forkJoin([ppwObs, teamObs])
            .subscribe(
              data => {

                this.ppw = data[0];
                this.teams = data[1];

                if (!this.teams || this.teams.length < 1) {

                  this.dialogService.alert("No Teams Available", "Please navigate to the 'Teams' tab and create a team before creating your first Campaign.", this.viewRef)
                    .then(
                      res => {

                        this.navigateUp();
                      }
                    )
                }

                this.route.queryParams
                  .subscribe(
                    params => {
                      if (params.c == 'true') {
                        this.cloning = true;
                        this.eventId = 0;
                        this.ppw.event.name += ' COPY';
                      }
                    }
                  );

              this.createForm(this.ppw);
            });
        }
      )
  }

  createForm(ppw: PledgePerWin) {

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    this.form = this.fb.group({
        pledgePerWinId: [ppw?.pledgePerWinId || 0, [Validators.required]],
        statistic: [ppw?.statistic || '', Validators.required],
        event : this.fb.group({
            eventId: [ppw?.event.eventId || 0, [Validators.required]],
            name: [ppw?.event.name || '', [Validators.required, Validators.maxLength(30)]],
            fundraisingGoal: [(ppw?.event.fundraisingGoal || 0) / 100, [Validators.required, Validators.max(1000000)]],
            realEventPeriod: [{ value: { startDate: ppw?.event.realStartDateTime ? new Date(ppw?.event.realStartDateTime) : today, endDate: ppw?.event.realEndDateTime ? new Date(ppw?.event.realEndDateTime) : tomorrow, isNew: false }, disabled: false }, Validators.required],
        }),
        teamId: [ppw?.teamId || 0, [Validators.required, Validators.min(1)]],
        newSubscriberEmailMessage: [ppw?.newSubscriberEmailMessage || this.ppwCopyPlaceholders.newSubscriberEmailMessage, [Validators.required, Validators.maxLength(300)]],
        gameOverEmailMessage: [ppw?.gameOverEmailMessage || this.ppwCopyPlaceholders.gameOverEmailMessage, [Validators.required, Validators.maxLength(300)]],
        subscriptionChargeEmailMessage: [ppw?.subscriptionChargeEmailMessage || this.ppwCopyPlaceholders.subscriptionChargeEmailMessage, [Validators.required, Validators.maxLength(300)]],
        gameOverTextMessage: [ppw?.gameOverTextMessage || this.ppwCopyPlaceholders.gameOverTextMessage, [Validators.required, Validators.maxLength(160)]],
        subscriptionChargeTextMessage: [ppw?.subscriptionChargeTextMessage || this.ppwCopyPlaceholders.subscriptionChargeTextMessage, [Validators.required, Validators.maxLength(160)]],
        sortOrder: [ppw?.sortOrder || 0, [Validators.required, Validators.min(1)]],
        customCheckoutCopy: [ppw?.customCheckoutCopy || this.ppwCopyPlaceholders.customCheckoutCopty, [Validators.required, Validators.maxLength(300)]],
        pledgePerWinPricingTiers: this.createPricingTierFormGroup(ppw?.pledgePerWinPricingTiers)
    });

    if (ppw?.event.status > 2 && !this.cloning){
      this.viewing = true;
      for (let control in this.form.controls){
        this.form.controls[control].disable();
      }
    }

    if (ppw?.event.status === 2) {

      this.form.controls.statistic.disable();
      this.form.controls.teamId.disable();
      (this.form.controls.event as FormGroup).controls.realEventPeriod.disable();
    }

    this.vendorName = this.getVendorName();
  }

  compare(val1, val2) {
    return val1 === val2;
  }

  removeLeadingZeros(event: KeyboardEvent, control: AbstractControl) {
    const parsedValue = Math.round((parseFloat(control?.value) + Number.EPSILON) * 100) / 100;
    control.patchValue(parsedValue.toFixed(2));
  }

  getEndDateTime() {
    const t = (!this.eventFormControls('realEventPeriod')?.invalid ? formatDateTime(this.eventFormControls('realEventPeriod')?.value.endDate) : new Date())
      + 'T' + (!this.formControls('winnerSelectTime').invalid ? (this.formControls('winnerSelectTime').value) : '12:00:00')  ;
    return t;
  }

  getStartDateTime() {
    const t = (!this.eventFormControls('realEventPeriod')?.invalid ? formatDateTime(this.eventFormControls('realEventPeriod')?.value.startDate) : new Date())
      + 'T' + (!this.formControls('eventStartTime').invalid ? (this.formControls('eventStartTime').value) : '12:00:00')  ;
    return t;
  }

  getVendorName() {
    return localStorage.getItem('vendorName');
  }

  save() {
    this.hasValidationErrors = !this.form.valid;
    const invalid = [];
    const controls = this.form.controls;
    for (const name in controls) {
        if (controls[name].invalid) {
            invalid.push(name);
        }
    }
    if (this.hasValidationErrors) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      return;
    }

    this.spinner.show();

    const ppwData = new PledgePerWin(this.form.getRawValue());
    ppwData.event.realStartDateTime = this.eventFormControls('realEventPeriod')?.value.startDate.toJSON();
    ppwData.event.realEndDateTime = this.eventFormControls('realEventPeriod')?.value.endDate.toJSON();
    ppwData.pledgePerWinPricingTiers.map(t => t.price = Math.floor(+t.price * 100));
    ppwData.event.fundraisingGoal = Math.floor(+ppwData.event.fundraisingGoal * 100);

    if(this.eventId > 0) {
      this.ppwService.update(ppwData).subscribe(
        () => {
          this.spinner.hide();
          this.navigateUp();
        },
        error => {
          this.spinner.hide();
          this.dialogService.error(error, "Unable to update Pledge Per Win", this.viewRef);
        }
      );
    } else {
      this.ppwService.create(ppwData).subscribe(
        () => {
          this.spinner.hide();
          this.router.navigate([`/portal/fundraising/ppw/event`])
        },
        error => {
          this.spinner.hide();
          this.dialogService.error(error, "Unable to create Pledge Per Win", this.viewRef);
        }
      );
    }
  }

  newPricingTier() {

    (<FormArray>this.form.controls.pledgePerWinPricingTiers)
      .push(
        this.fb.group({
          price: [null, Validators.required]
        })
      );
    this.form.controls.pledgePerWinPricingTiers.markAsDirty();
    this.form.updateValueAndValidity();
  }

  createPricingTierFormGroup(tiers: PledgePerWinPricingTier[]) {

    const f = new FormArray<FormGroup>([]);

    if (tiers && tiers.length > 0) {
      tiers.forEach(q =>
        f.push(this.fb.group({
          pledgePerWinPricingTierId: [q.pledgePerWinPricingTierId],
          price: [{value: +q.price / 100, disabled: this.ppw?.event.status === 2 && q.pledgePerWinPricingTierId}, [Validators.required]]
        }))
      );
    } else {
      f.push(this.fb.group({
        price: [10, Validators.required]
      }));
    }

    return f;
  }

  deletePricingTier(tierIndex: number) {
    (<FormArray>this.form.controls.pledgePerWinPricingTiers).removeAt(tierIndex);
    this.form.controls.pledgePerWinPricingTiers.markAsDirty();
    this.form.updateValueAndValidity();
  }

  public navigateUp() {
    this.router.navigate([this.eventId > 0 || this.cloning ? '../../' : '../'], {relativeTo: this.route});
  }

}
